import { useState, useCallback } from 'react'
import styled from 'styled-components'
import { Currency, Pair, TokenAmount } from 'vvs-sdk'
import { Button, Text, Flex, Box, useModal } from 'vvs-uikit'
import { useTranslation } from 'contexts/Localization'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { useFarms, usePriceVvsUsdc, useLpTokenPrice, useTokenPricesUsdc } from 'state/farms/hooks'
import { TokenPairImage } from 'components/TokenImage'
import BigNumber from 'bignumber.js'
import { BIG_ZERO } from 'utils/bigNumber'
import { getFarmAprByVersion } from 'utils/apr'
import { getAddress } from 'utils/addressHelpers'
import { getDisplayApr } from 'views/Farms/utils'
import getLiquidityUrlPathParts from 'utils/getLiquidityUrlPathParts'
import { fetchFarmUserDataAsync } from 'state/farms'
import { DeserializedFarm } from 'state/types'
import { useAppDispatch } from 'state'
import { BASE_ADD_LIQUIDITY_URL } from 'config'
import { getCrc20Contract } from 'utils/contractHelpers'
import useApproveFarm from 'views/Farms/hooks/useApproveFarm'
import useStakeFarms from 'views/Farms/hooks/useStakeFarms'
import useStakeFarmsV2 from 'views/Farms/hooks/useStakeFarmsV2'
import useApproveFarmV2 from 'views/Farms/hooks/useApproveFarmV2'

import { getLpUsdcPrice } from 'utils/farmsPriceHelpers'
import { getBalanceAmount } from 'utils/formatBalance'
import DepositModal from '../../views/Farms/components/DepositModal'

const Divider = styled.div`
  background-color: ${({ theme }) => theme.colors.cardBorder};
  height: 1px;
  margin: 20px 0;
  width: 100%;
`
const TokenWrapper = styled.div`
  top: 4px;
  position: relative;

  ${({ theme }) => theme.mediaQueries.sm} {
    width: 60px;
  }
`

export function StakeInFarm({
  source,
  farm,
  userPoolBalance,
  pair,
  currency0,
  currency1,
}: {
  source: string
  farm: DeserializedFarm
  userPoolBalance: TokenAmount
  pair: Pair
  currency0: Currency
  currency1: Currency
}) {
  const { data: farmsLp } = useFarms()
  const { account, library } = useActiveWeb3React()
  const dispatch = useAppDispatch()
  const pid = farm?.pid
  const lpLabel = farm?.lpSymbol && farm?.lpSymbol.toUpperCase()
  const tokenName = farm?.lpSymbol
  const lpPrice = useLpTokenPrice(tokenName ?? 'VVS', farm?.version)
  const token = farm?.token
  const quoteToken = farm?.quoteToken
  const { allowance, stakedBalance } = farm?.userData || {}
  const isApproved = account && allowance && allowance.isGreaterThan(0)
  const lpContract = farm && getCrc20Contract(getAddress(farm.lpAddresses), library.getSigner())
  const { onApprove } = useApproveFarm(lpContract)
  const { onApprove: onApproveV2 } = useApproveFarmV2(lpContract)
  const { onStake } = useStakeFarms(farm?.pid)
  const { onStake: onStakeV2 } = useStakeFarmsV2(pid)
  const vvsPrice = usePriceVvsUsdc()
  const tokenPrices = useTokenPricesUsdc(farmsLp)
  const { t } = useTranslation()

  let apr = null
  let vvsApr = null
  let lpApr = null
  let addLiquidityUrl = null
  let displayApr = null
  let totalLiquidity = null
  if (farm) {
    totalLiquidity = new BigNumber(farm.lpTotalInQuoteToken).times(farm.quoteTokenPriceUsdc)

    const lpTokenPrice: BigNumber = getLpUsdcPrice(farm)
    const stakedLiquidityInUSD =
      farm.version === 2 && farm.migration
        ? getBalanceAmount(farm.lpTokenAmountInCraftsmanV2).times(lpTokenPrice)
        : totalLiquidity
    const {
      vvsRewardsApr: vapr,
      tokenRewardsApr: tapr,
      lpRewardsApr: lapr,
    } = getFarmAprByVersion(
      farm.poolWeight,
      vvsPrice,
      totalLiquidity,
      getAddress(farm.lpAddresses),
      farm.version,
      farm.rewardData,
      stakedLiquidityInUSD,
      tokenPrices,
    )
    apr = tapr
    vvsApr = vapr
    lpApr = lapr
    const liquidityUrlPathParts = getLiquidityUrlPathParts({
      quoteTokenAddress: farm.quoteToken.address,
      tokenAddress: farm.token.address,
    })
    displayApr = getDisplayApr(apr, lpApr)
    addLiquidityUrl = `${BASE_ADD_LIQUIDITY_URL}/${liquidityUrlPathParts}`
  }

  const handleStake = async (amount: string) => {
    if (farm.version === 2) {
      await onStakeV2(amount)
    } else {
      await onStake(amount)
    }
    dispatch(fetchFarmUserDataAsync({ account, pids: [pid] }))
  }
  const [requestedApproval, setRequestedApproval] = useState(false)
  const [onPresentDeposit] = useModal(
    <DepositModal
      max={userPoolBalance?.raw?.toString() ? new BigNumber(userPoolBalance?.raw?.toString()) : BIG_ZERO}
      stakedBalance={stakedBalance}
      onConfirm={handleStake}
      tokenName={tokenName}
      lpPrice={lpPrice}
      lpLabel={lpLabel}
      apr={apr}
      vvsApr={vvsApr}
      displayApr={displayApr}
      addLiquidityUrl={addLiquidityUrl}
      vvsPrice={vvsPrice}
      token={token}
      quoteToken={quoteToken}
      source={source}
      poolLiquidity={totalLiquidity}
      rewardData={farm?.rewardData}
      farm={farm}
      farms={farmsLp}
    />,
    false,
  )
  const handleApprove = useCallback(async () => {
    try {
      const { version } = farm
      setRequestedApproval(true)
      if (version === 2) {
        await onApproveV2()
      } else {
        await onApprove()
      }
      dispatch(fetchFarmUserDataAsync({ account, pids: [pid] }))

      setRequestedApproval(false)
    } catch (e) {
      console.error(e)
    }
  }, [onApprove, onApproveV2, dispatch, account, pid, farm])

  return (
    <>
      <Divider />
      <Flex flexDirection="row" justifyContent="space-between">
        <Flex flexDirection="column">
          <Flex flexDirection="row" alignItems="center">
            <TokenWrapper>
              <TokenPairImage
                variant="inverted"
                primaryToken={pair.token0}
                secondaryToken={pair.token1}
                width={32}
                height={32}
              />
            </TokenWrapper>
            <Box ml="-10px">
              <Text small color="textSubtle">
                {t('%token0Symbol%-%token1Symbol% Crystal Farm', {
                  token0Symbol: currency0.symbol,
                  token1Symbol: currency1.symbol,
                })}
              </Text>
            </Box>
          </Flex>
          <Box mt="5px">
            <Text color="bold" small textTransform="uppercase">
              {t('apr: %apr%%', {
                apr: displayApr ?? 0,
              })}
            </Text>
          </Box>
        </Flex>
        {isApproved ? (
          <Button variant="secondary" onClick={onPresentDeposit}>
            <Text color="#46A1C2" bold as="h3">
              {t('Stake in Farm')}
            </Text>
          </Button>
        ) : (
          <Button disabled={requestedApproval} onClick={handleApprove} variant="secondary">
            {t('Enable')}
          </Button>
        )}
      </Flex>
    </>
  )
}
